import { useCallback, useEffect, useState } from "react";

const CURSOR_HIDDEN_POSITION = { x: -100, y: -100 };
const PARALLAX_X_OFFSET = -8;
const PARALLAX_Y_OFFSET = -6;
const PARALLAX_SCALE = 1.03;

const getParallaxTransform = (event) => {
  const mouseX = (event.clientX / window.innerWidth - 0.5) * 2;
  const mouseY = (event.clientY / window.innerHeight - 0.5) * 2;
  return `translate(${mouseX * PARALLAX_X_OFFSET}px, ${mouseY * PARALLAX_Y_OFFSET}px) scale(${PARALLAX_SCALE})`;
};

const attachLogoHoverListeners = (logos, onEnter, onLeave) => {
  logos.forEach((element) => {
    element.addEventListener("mouseenter", onEnter);
    element.addEventListener("mouseleave", onLeave);
  });

  return () => {
    logos.forEach((element) => {
      element.removeEventListener("mouseenter", onEnter);
      element.removeEventListener("mouseleave", onLeave);
    });
  };
};

const useScrollReset = (scrollToTopOnMount) => {
  useEffect(() => {
    if (!scrollToTopOnMount) {
      return undefined;
    }

    window.scrollTo({ top: 0, behavior: "auto" });
    return undefined;
  }, [scrollToTopOnMount]);
};

const useCursorTracking = ({ backgroundRef, enabled, setCursorPos }) => {
  const handleMouseMove = useCallback((event) => {
    setCursorPos({ x: event.clientX, y: event.clientY });

    if (!backgroundRef?.current) {
      return;
    }

    backgroundRef.current.style.transform = getParallaxTransform(event);
  }, [backgroundRef, setCursorPos]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [enabled, handleMouseMove]);
};

const useLogoHoverTracking = ({ enabled, setIsHoveringLogo }) => {
  const handleLogoEnter = useCallback(() => setIsHoveringLogo(true), [setIsHoveringLogo]);
  const handleLogoLeave = useCallback(() => setIsHoveringLogo(false), [setIsHoveringLogo]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const logos = Array.from(document.querySelectorAll(".logo-hover-target"));
    return attachLogoHoverListeners(logos, handleLogoEnter, handleLogoLeave);
  }, [enabled, handleLogoEnter, handleLogoLeave]);
};

export const useSiteCursor = ({ enabled = true, backgroundRef = null, scrollToTopOnMount = false } = {}) => {
  const [cursorPos, setCursorPos] = useState(CURSOR_HIDDEN_POSITION);
  const [isHoveringLogo, setIsHoveringLogo] = useState(false);

  useScrollReset(scrollToTopOnMount);
  useCursorTracking({ backgroundRef, enabled, setCursorPos });
  useLogoHoverTracking({ enabled, setIsHoveringLogo });

  return { cursorPos, isHoveringLogo };
};